/**
 * Response Handler Utilities
 * Standardizes response handling across all API endpoints
 */

/**
 * Extract data from various response formats
 * @param {Object} response - API response object
 * @param {string} expectedKey - Primary key to look for (e.g., 'data', 'records', 'stocks')
 * @returns {any} Extracted data or null
 */
export const extractData = (response, expectedKey = 'data') => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  // Try the expected key first
  if (response[expectedKey] !== undefined) {
    return response[expectedKey];
  }

  // Try common fallbacks
  if (response.data !== undefined) {
    return response.data;
  }

  if (response.records !== undefined) {
    return response.records;
  }

  return null;
};

/**
 * Extract array data from response, handling multiple formats
 * @param {Object} response - API response object
 * @param {Array<string>} possibleKeys - Keys to check in order
 * @returns {Array} Array data or empty array
 */
export const extractArrayData = (response, possibleKeys = ['data', 'records', 'stocks']) => {
  if (!response || typeof response !== 'object') {
    return [];
  }

  for (const key of possibleKeys) {
    if (Array.isArray(response[key])) {
      return response[key];
    }
  }

  return [];
};

/**
 * Extract nested object data for sector/stock data
 * @param {Object} response - API response object
 * @returns {Object} Data object with known fields
 */
export const extractSectorData = (response) => {
  if (!response || typeof response !== 'object') {
    return { stocks: [], stats: {} };
  }

  let stocks = [];
  let stats = {};

  // Try multiple stock data structures
  if (Array.isArray(response.ranked_stocks)) {
    stocks = response.ranked_stocks;
  } else if (Array.isArray(response.abnormal_growth_stocks)) {
    stocks = response.abnormal_growth_stocks;
  } else if (Array.isArray(response.stocks)) {
    stocks = response.stocks;
  } else if (response.data && typeof response.data === 'object') {
    // Check nested data object
    if (Array.isArray(response.data.ranked_stocks)) {
      stocks = response.data.ranked_stocks;
    } else if (Array.isArray(response.data.abnormal_growth_stocks)) {
      stocks = response.data.abnormal_growth_stocks;
    } else if (Array.isArray(response.data.stocks)) {
      stocks = response.data.stocks;
    }

    // Extract stats from data object
    if (response.data.sector_stats && typeof response.data.sector_stats === 'object') {
      stats = response.data.sector_stats;
    }
  }

  return { stocks, stats };
};

/**
 * Extract PCR/OI data from response
 * @param {Object} response - API response object
 * @returns {Object} PCR data object
 */
export const extractPCRData = (response) => {
  if (!response || typeof response !== 'object') {
    return {};
  }

  // Try data object first
  if (response.data && typeof response.data === 'object') {
    return response.data;
  }

  // Otherwise return the whole response
  return response;
};

/**
 * Extract breakout/performance data with top and low performers
 * @param {Object} response - API response object
 * @returns {Object} Object with topPerformers and lowPerformers arrays
 */
export const extractPerformanceData = (response) => {
  if (!response || typeof response !== 'object') {
    return { topPerformers: [], lowPerformers: [] };
  }

  let topPerformers = [];
  let lowPerformers = [];

  // Check nested data structure
  if (response.data && typeof response.data === 'object') {
    if (Array.isArray(response.data.top_performers)) {
      topPerformers = response.data.top_performers;
    }
    if (Array.isArray(response.data.low_performers)) {
      lowPerformers = response.data.low_performers;
    }
  }

  // Check top-level structure
  if (Array.isArray(response.top_performers)) {
    topPerformers = response.top_performers;
  }
  if (Array.isArray(response.low_performers)) {
    lowPerformers = response.low_performers;
  }

  return { topPerformers, lowPerformers };
};

/**
 * Extract FII/DII records from response
 * @param {Object} response - API response object
 * @returns {Array} Records array
 */
export const extractRecords = (response) => {
  if (!response || typeof response !== 'object') {
    return [];
  }

  if (Array.isArray(response.records)) {
    return response.records;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  if (response.data && Array.isArray(response.data.records)) {
    return response.data.records;
  }

  return [];
};

/**
 * Check if response indicates success
 * @param {Object} response - API response object
 * @returns {boolean} True if response indicates success
 */
export const isSuccessResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  return response.status === 'success' || response.success === true;
};

/**
 * Extract error message from response
 * @param {Object} response - API response object
 * @param {string} defaultMessage - Default error message
 * @returns {string} Error message
 */
export const extractErrorMessage = (response, defaultMessage = 'Unknown error') => {
  if (!response || typeof response !== 'object') {
    return defaultMessage;
  }

  return response.message || response.error || defaultMessage;
};

/**
 * Safe nested property accessor with fallbacks
 * @param {Object} obj - Object to access
 * @param {Array<string>} keys - Keys in order to check
 * @param {any} defaultValue - Default value if not found
 * @returns {any} Value or default
 */
export const safeGet = (obj, keys, defaultValue = null) => {
  if (!obj || typeof obj !== 'object') {
    return defaultValue;
  }

  let current = obj;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }

  return current;
};
